import { ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/utils/initials-format";
import { cn } from "@/lib/utils";

interface PersonAvatarViewProps {
  nome: string;
  /** URL da foto já resolvida (thumb ou original). */
  foto?: string;
  /** Classe extra no Avatar (ex.: tamanho, grayscale de ex-colaborador). */
  className?: string;
  /** Classe do fallback de iniciais (bg + cor). Ex.: `bg-primary/10 text-primary`. */
  fallbackClassName?: string;
  /** Conteúdo quando não há iniciais (ex.: ícone `<User />`). Default: iniciais vazias. */
  emptyFallback?: ReactNode;
  /** Overlay posicionado sobre o avatar (ex.: badge de férias). */
  overlay?: ReactNode;
}

/**
 * Avatar de pessoa **puramente presentacional**: foto (com fallback de iniciais)
 * + slot de overlay. Compartilhado entre o `Person` (wrapper rico com
 * HoverCard/dialog) e o `PessoaOption` (item de combobox).
 *
 * Renderiza só `<span>` (o `Avatar` do Radix é `span` e já é `relative`) — por
 * isso é válido dentro do `<button>` do gatilho do combobox.
 */
export function PersonAvatarView({
  nome,
  foto,
  className,
  fallbackClassName,
  emptyFallback,
  overlay,
}: Readonly<PersonAvatarViewProps>) {
  const iniciais = getInitials(nome);

  return (
    <span className="relative inline-flex shrink-0">
      {/*
        `key` força o remount ao trocar de pessoa: o Avatar do Radix retém o
        status da imagem no Root (`loaded`) e, ao reaproveitar o mesmo nó para
        alguém sem foto, o Fallback (iniciais) não voltava a aparecer.
      */}
      <Avatar
        key={foto ?? nome}
        className={cn("h-7 w-7 shrink-0 border border-border shadow-sm", className)}
      >
        {foto && <AvatarImage src={foto} alt={nome} className="object-cover" />}
        <AvatarFallback className={cn("text-[10px] font-semibold", fallbackClassName)}>
          {iniciais || emptyFallback}
        </AvatarFallback>
      </Avatar>
      {overlay}
    </span>
  );
}
